import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ClipboardPaste, Database, Loader2, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../../lib/api";

const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet transition-colors";

export default function UnitsTab({ plan, onChange }) {
  const [units, setUnits] = useState(null);
  const [filter, setFilter] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [busy, setBusy] = useState(false);
  const vars = plan.variables || [];

  const load = useCallback(() => {
    api.get(`/planning/plans/${plan.id}/work-units`).then((r) => setUnits(r.data)).catch(() => setUnits([]));
  }, [plan.id]);
  useEffect(() => { load(); }, [load]);

  const addRow = async () => {
    const row = Object.fromEntries(vars.map((v) => [v.code, v.type === "integer" || v.type === "decimal" ? 0 : ""]));
    try { await api.post(`/planning/plans/${plan.id}/work-units`, { rows: [row] }); load(); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  const paste = async () => {
    const lines = pasteText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return;
    let start = 0;
    const firstCells = lines[0].split("\t").map((c) => c.trim().toLowerCase());
    if (vars.some((v) => firstCells.includes(v.name.toLowerCase()) || firstCells.includes(v.code.toLowerCase()))) start = 1;
    const rows = lines.slice(start).map((l) => {
      const cells = l.split("\t");
      return Object.fromEntries(vars.map((v, i) => [v.code, cells[i] ?? ""]));
    });
    setBusy(true);
    try {
      const { data } = await api.post(`/planning/plans/${plan.id}/work-units`, { rows });
      toast.success(`${data.added} unidades agregadas`);
      setPasteOpen(false); setPasteText(""); load(); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al cargar"); }
    finally { setBusy(false); }
  };

  const loadDemo = async () => {
    if (!window.confirm("Esto reemplaza las unidades actuales con las 120 unidades del caso de prueba. ¿Continuar?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/planning/plans/${plan.id}/work-units/demo`);
      toast.success(`${data.added} unidades demo cargadas`); load(); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setBusy(false); }
  };

  const saveCell = async (wu, code, value) => {
    if (String(wu.values[code] ?? "") === String(value)) return;
    try {
      await api.patch(`/planning/work-units/${wu.id}`, { values: { [code]: value } });
      setUnits((prev) => prev.map((u) => u.id === wu.id ? { ...u, values: { ...u.values, [code]: value } } : u));
    } catch { toast.error("No se pudo guardar"); }
  };

  const del = async (wu) => {
    try { await api.delete(`/planning/work-units/${wu.id}`); load(); onChange(); } catch { toast.error("Error"); }
  };

  const view = useMemo(() => {
    if (!units) return [];
    let list = units;
    if (filter) {
      const f = filter.toLowerCase();
      list = list.filter((u) => Object.values(u.values).some((v) => String(v).toLowerCase().includes(f)));
    }
    if (sortBy) {
      const { code, dir } = sortBy;
      list = [...list].sort((a, b) => {
        const av = a.values[code], bv = b.values[code];
        const an = parseFloat(av), bn = parseFloat(bv);
        const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av).localeCompare(String(bv));
        return dir === "asc" ? cmp : -cmp;
      });
    }
    return list;
  }, [units, filter, sortBy]);

  if (units === null) return <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-navy/30" /></div>;

  return (
    <div className="space-y-3" data-testid="units-tab">
      <div className="flex flex-wrap items-center gap-2">
        <input className={`${inputCls} !w-56`} placeholder="Buscar…" value={filter} data-testid="units-filter"
          onChange={(e) => setFilter(e.target.value)} />
        <span className="text-xs text-navy/40">{view.length} de {units.length} {plan.work_unit_name || "unidades"}(s)</span>
        <div className="ml-auto flex gap-2">
          {plan.template === "infraestructura" && (
            <button onClick={loadDemo} disabled={busy} data-testid="load-demo-btn"
              className="rounded-full border border-navy/15 text-sm px-4 py-2 hover:border-violet inline-flex items-center gap-1.5">
              <Database className="w-4 h-4" /> Cargar caso de prueba (120)
            </button>
          )}
          <button onClick={() => setPasteOpen(!pasteOpen)} data-testid="paste-btn"
            className="rounded-full border border-navy/15 text-sm px-4 py-2 hover:border-violet inline-flex items-center gap-1.5">
            <ClipboardPaste className="w-4 h-4" /> Pegar desde spreadsheet
          </button>
          <button onClick={addRow} data-testid="add-row-btn"
            className="rounded-full bg-navy text-cream text-sm px-4 py-2 hover:bg-navy-soft inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Agregar fila
          </button>
        </div>
      </div>

      {pasteOpen && (
        <div className="rounded-xl border border-navy/10 bg-white p-4 space-y-2" data-testid="paste-panel">
          <p className="text-xs text-navy/50">Copia filas desde Excel/Sheets y pégalas aquí. Orden de columnas: {vars.map((v) => v.name).join(" · ")} (con o sin encabezado).</p>
          <textarea className={`${inputCls} font-mono text-xs`} rows={6} value={pasteText} data-testid="paste-textarea"
            placeholder={"ML46C\tProvincia\t1\t13\nML47C\tProvincia\t1\t13"} onChange={(e) => setPasteText(e.target.value)} />
          <button onClick={paste} disabled={busy || !pasteText.trim()} data-testid="paste-submit"
            className="rounded-full bg-navy text-cream text-sm px-4 py-2 hover:bg-navy-soft disabled:opacity-50 inline-flex items-center gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Cargar filas
          </button>
        </div>
      )}

      <div className="rounded-xl border border-navy/10 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-navy/10 text-left text-xs text-navy/40">
              {vars.map((v) => (
                <th key={v.code} className="px-3 py-2.5 font-medium cursor-pointer select-none hover:text-violet whitespace-nowrap"
                  onClick={() => setSortBy((s) => s?.code === v.code && s.dir === "desc" ? { code: v.code, dir: "asc" } : { code: v.code, dir: "desc" })}>
                  {v.name}{sortBy?.code === v.code ? (sortBy.dir === "desc" ? " ↓" : " ↑") : ""}
                </th>
              ))}
              <th className="px-3 py-2.5 font-medium">Estado</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {view.map((wu) => (
              <tr key={wu.id} className="border-b border-navy/5 hover:bg-navy/[0.02]" data-testid={`wu-row-${wu.id}`}>
                {vars.map((v) => (
                  <td key={v.code} className="px-1 py-0.5">
                    {v.type === "select" ? (
                      <select className="w-full bg-transparent px-2 py-1.5 text-sm outline-none focus:bg-white rounded"
                        value={wu.values[v.code] ?? ""} onChange={(e) => saveCell(wu, v.code, e.target.value)}>
                        <option value="" />
                        {(v.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input className="w-full bg-transparent px-2 py-1.5 text-sm outline-none focus:bg-white rounded min-w-[70px]"
                        defaultValue={wu.values[v.code] ?? ""} type={["integer", "decimal"].includes(v.type) ? "number" : "text"}
                        onBlur={(e) => saveCell(wu, v.code, e.target.value)} />
                    )}
                  </td>
                ))}
                <td className="px-3 py-1.5 text-xs text-navy/50 whitespace-nowrap">{(wu.status || "pendiente").replaceAll("_", " ")}</td>
                <td className="px-2"><button onClick={() => del(wu)} className="text-navy/20 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button></td>
              </tr>
            ))}
            {view.length === 0 && (
              <tr><td colSpan={vars.length + 2} className="px-4 py-10 text-center text-sm text-navy/40">
                Sin unidades. Agrega filas, pega desde spreadsheet{plan.template === "infraestructura" ? " o carga el caso de prueba" : ""}.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
